// Pony's GBFS geofencing zones (geofencing_zones.json, GBFS 2.2) as the map and the ride use them:
// the operating zone (outside it nothing is allowed: global_rules), slow zones, and the red zones.
import type { Position, VehicleType } from "./api";
import { inPolygon } from "./parking";

/** [lng, lat] rings: the first is the outline, the others are holes. */
export type Polygon = [number, number][][];
/** A GeoJSON MultiPolygon's coordinates: islands, each with its holes. */
export type Area = Polygon[];

export type ZoneKind = "operating" | "slow" | "noParking" | "noRide";

export interface Zone {
  id: string;
  kind: ZoneKind;
  area: Area;
  /** Slow zones: the speed limit per vehicle type, km/h. */
  maxKmh?: Partial<Record<VehicleType, number>>;
}

interface GbfsRule {
  vehicle_type_id?: string[];
  ride_allowed?: boolean;
  ride_start_allowed?: boolean;
  ride_end_allowed?: boolean;
  ride_through_allowed?: boolean;
  maximum_speed_kph?: number;
  station_parking?: boolean;
}
export interface GbfsZoneFeature {
  type?: "Feature";
  geometry: { type: "MultiPolygon"; coordinates: [number, number][][][] };
  properties: {
    name?: string | undefined;
    station_id?: string | undefined;
    rules?: GbfsRule[] | undefined;
  };
}

/** Above this a limit is a slow zone; Pony sets 1 km/h on the no-ride zones. */
const SLOW_MIN_KMH = 2;

/**
 * Which kind of zone a feature is, as pony-core writes them (lib/helpers/gbfs.py):
 * - "operating_zone", "slow_zones";
 * - red zones named `red_zones_parking_{not no_parking}_riding_{not no_riding}`, with
 *   ride_allowed / ride_start_allowed / ride_end_allowed = not no_riding and — Pony's own use of a
 *   GBFS field — ride_through_allowed = not no_parking.
 * Riding forbidden is a no-riding zone (no ride end there either: no parking a fortiori); parking
 * forbidden alone is a no-parking zone. Without a Pony name, the same flags decide. Station areas
 * (station_id) are parkings, read from station_information: not zones.
 */
export function classifyZone(f: GbfsZoneFeature): ZoneKind | undefined {
  const name = f.properties.name ?? "";
  const rules = f.properties.rules ?? [];
  if (/^operating_zone/i.test(name)) return "operating";
  if (/^slow_zone/i.test(name)) return "slow";
  const red = /^red_zones?_parking_(true|false)_riding_(true|false)/i.exec(name);
  if (red) {
    if (red[2]?.toLowerCase() === "false") return "noRide";
    if (red[1]?.toLowerCase() === "false") return "noParking";
    return undefined; // nothing forbidden
  }
  if (f.properties.station_id) return undefined;
  const noRiding = (r: GbfsRule) =>
    r.ride_allowed === false || r.ride_start_allowed === false || r.ride_end_allowed === false;
  if (rules.some(noRiding)) return "noRide";
  if (rules.some((r) => r.ride_through_allowed === false)) return "noParking";
  if (rules.some((r) => (r.maximum_speed_kph ?? 0) >= SLOW_MIN_KMH)) return "slow";
  if (rules.some((r) => r.station_parking)) return "operating";
  return undefined;
}

const TYPES: VehicleType[] = ["ebike", "bike", "scooter"];

const isPoint = (c: unknown): c is [number, number] =>
  Array.isArray(c) &&
  typeof c[0] === "number" &&
  typeof c[1] === "number" &&
  Math.abs(c[0]) <= 180 &&
  Math.abs(c[1]) <= 90;
const isRing = (r: unknown): r is [number, number][] =>
  Array.isArray(r) && r.length >= 3 && r.length <= 50_000 && r.every(isPoint);

/** The feed's MultiPolygon if well formed (islands of rings of [lng, lat]), else undefined. */
function validArea(c: unknown): Area | undefined {
  if (!Array.isArray(c) || c.length === 0) return undefined;
  const ok = c.every((poly) => Array.isArray(poly) && poly.length > 0 && poly.every(isRing));
  return ok ? (c as Area) : undefined;
}

/** The zones of one region's feed; ids are `<region>:<kind>:<n>`. */
export function zonesFromGbfs(features: GbfsZoneFeature[], region: string): Zone[] {
  const out: Zone[] = [];
  for (const f of features) {
    const kind = classifyZone(f);
    const area = validArea(f.geometry?.coordinates);
    if (!kind || !area) continue;
    const zone: Zone = { id: `${region}:${kind}:${out.length}`, kind, area };
    if (kind === "slow") {
      const maxKmh: Partial<Record<VehicleType, number>> = {};
      for (const r of f.properties.rules ?? [])
        for (const t of r.vehicle_type_id ?? TYPES)
          if (TYPES.includes(t as VehicleType) && (r.maximum_speed_kph ?? 0) >= SLOW_MIN_KMH)
            maxKmh[t as VehicleType] = r.maximum_speed_kph as number;
      zone.maxKmh = maxKmh;
    }
    out.push(zone);
  }
  return out;
}

/** Inside one of the islands, and not in one of its holes. */
export function inArea(p: Position, area: Area): boolean {
  return area.some(
    ([outline, ...holes]) =>
      !!outline && inPolygon(p, outline) && !holes.some((h) => inPolygon(p, h)),
  );
}

export interface ZonesHere {
  /** Off the operating zone (nothing allowed there); false when no operating zone is published. */
  outside: boolean;
  noRide: boolean;
  noParking: boolean;
  /** In a slow zone: its limit for this vehicle type. */
  maxKmh?: number;
}

export function zonesAt(p: Position, zones: Zone[], type: VehicleType): ZonesHere {
  const operating = zones.filter((z) => z.kind === "operating");
  const here = zones.filter((z) => z.kind !== "operating" && inArea(p, z.area));
  const noRide = here.some((z) => z.kind === "noRide");
  const out: ZonesHere = {
    outside: operating.length > 0 && !operating.some((z) => inArea(p, z.area)),
    noRide,
    noParking: noRide || here.some((z) => z.kind === "noParking"),
  };
  const limits = here
    .filter((z) => z.kind === "slow")
    .map((z) => z.maxKmh?.[type] ?? Math.min(...Object.values(z.maxKmh ?? {})))
    .filter((n) => Number.isFinite(n));
  if (limits.length) out.maxKmh = Math.min(...limits);
  return out;
}

// ---- Map rendering (Figma K1) -------------------------------------------------------------------

const WORLD_RING: [number, number][] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

/**
 * The shaded "outside" (Figma K1, the PO's inverted polygon): the world with every operating island
 * as a hole, plus the operating zone's own holes (excluded areas) shaded again. Empty when no
 * operating zone is published.
 */
export function outsideArea(zones: Zone[]): Area {
  const islands = zones.filter((z) => z.kind === "operating").flatMap((z) => z.area);
  if (islands.length === 0) return [];
  const outlines = islands.map(([outline]) => outline).filter((o): o is [number, number][] => !!o);
  const holes = islands.flatMap(([, ...h]) => h.map((ring) => [ring]));
  return [[WORLD_RING, ...outlines], ...holes];
}

/** Where to put a zone's badge: per island, its centroid if inside, else the inside grid point nearest to it. */
export function badgePoints(area: Area): Position[] {
  const out: Position[] = [];
  for (const island of area) {
    const outline = island[0];
    if (!outline || outline.length < 3) continue;
    const lngs = outline.map(([lng]) => lng);
    const lats = outline.map(([, lat]) => lat);
    const c = {
      lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
      lat: lats.reduce((a, b) => a + b, 0) / lats.length,
    };
    if (inArea(c, [island])) {
      out.push(c);
      continue;
    }
    const [x0, x1, y0, y1] = [
      Math.min(...lngs),
      Math.max(...lngs),
      Math.min(...lats),
      Math.max(...lats),
    ];
    let best: Position | undefined;
    let bestD = Number.POSITIVE_INFINITY;
    const N = 24;
    for (let i = 1; i < N; i++)
      for (let j = 1; j < N; j++) {
        const p = { lng: x0 + ((x1 - x0) * i) / N, lat: y0 + ((y1 - y0) * j) / N };
        const d = (p.lng - c.lng) ** 2 + (p.lat - c.lat) ** 2;
        if (d < bestD && inArea(p, [island])) {
          best = p;
          bestD = d;
        }
      }
    if (best) out.push(best);
  }
  return out;
}

// ---- Sheets (K2–K5) and ride toasts ---------------------------------------------------------------

export type ZoneSheet =
  | { kind: "slow"; maxKmh?: number }
  | { kind: "noParking" }
  | { kind: "noRide" }
  | { kind: "outside" };

const sheetOf = (h: ZonesHere): ZoneSheet | undefined =>
  h.noRide
    ? { kind: "noRide" }
    : h.noParking
      ? { kind: "noParking" }
      : h.maxKmh !== undefined
        ? { kind: "slow", maxKmh: h.maxKmh }
        : h.outside
          ? { kind: "outside" }
          : undefined;

/** The sheet a tap at `p` opens: the strongest zone there (no riding > no parking > slow > outside). */
export function zoneSheetAt(p: Position, zones: Zone[], type: VehicleType): ZoneSheet | undefined {
  return sheetOf(zonesAt(p, zones, type));
}

/** Figma K2–K5 texts; the speed is the zone's own limit for the vehicle type. */
export function zoneSheetText(s: ZoneSheet): { title: string; subtitle: string; body: string } {
  switch (s.kind) {
    case "slow": {
      const kmh = s.maxKmh !== undefined ? `${s.maxKmh} km/h` : "une vitesse réduite";
      return {
        title: "Zone à vitesse réduite",
        subtitle: `Vitesse limitée à ${kmh}`,
        body: `La vitesse du vélo est limitée à ${kmh} dans cette zone. Il est interdit d'y stationner.`,
      };
    }
    case "noParking":
      return {
        title: "Stationnement interdit",
        subtitle: "Vous pouvez y rouler, pas vous y garer",
        body: "Il est interdit de stationner dans cette zone. Vous risquez une pénalité.",
      };
    case "noRide":
      return {
        title: "Zone interdite",
        subtitle: "Circulation et stationnement interdits",
        body: "Il est interdit de circuler dans cette zone : l'assistance électrique s'éteint et vous risquez une pénalité.",
      };
    case "outside":
      return {
        title: "Hors zone d'opération",
        subtitle: "Limite de la zone de service",
        body: "En dehors de la zone d'opération, l'assistance électrique s'éteint automatiquement. Vous ne pouvez pas y terminer votre trajet.",
      };
  }
}

/**
 * Ride toasts: the zones the bike just entered (from `prev`, undefined at the first position: where
 * it starts counts). The red zones toast once, the strongest one.
 */
export function zonesEntered(prev: ZonesHere | undefined, next: ZonesHere): ZoneSheet[] {
  const was = prev ?? { outside: false, noRide: false, noParking: false };
  const out: ZoneSheet[] = [];
  if (next.outside && !was.outside) out.push({ kind: "outside" });
  if (next.noRide && !was.noRide) out.push({ kind: "noRide" });
  else if (next.noParking && !was.noParking && !next.noRide) out.push({ kind: "noParking" });
  if (next.maxKmh !== undefined && was.maxKmh === undefined)
    out.push({ kind: "slow", maxKmh: next.maxKmh });
  return out;
}
